import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {Briefcase, Building2, Calendar, Trash2, Search, CheckCircle2, Clock, Eye, XCircle, MapPin, Phone, Info, AlarmClock, Check, X, } from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import {
  useApplications, type Application,
  APPLICATION_STATUS_LABELS, APPLICATION_STATUS_STYLES,
  INTERVIEW_STATUS_LABELS, INTERVIEW_STATUS_STYLES,
} from '@/hooks/useApplications'
import { useAuth } from '@/hooks/useAuth'

const statusFilters: Array<{ value: string; label: string }> = [
  { value: 'todas', label: 'Todas' },
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'revisado', label: 'Revisadas' },
  { value: 'entrevista', label: 'Entrevista' },
  { value: 'aceptado', label: 'Aceptadas' },
  { value: 'rechazado', label: 'Rechazadas' },
]

function fmtDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
}

function fmtDateTime(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-MX', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function ColaboradorPostulaciones() {
  const { user } = useAuth()
  const { applications, loading, fetchByCollaborator, withdrawApplication, updateInterview } = useApplications()
  const [filter, setFilter] = useState('todas')
  const [working, setWorking] = useState(false)
  const [interviewDetail, setInterviewDetail] = useState<Application | null>(null)

  const reload = useCallback(() => {
    if (user) fetchByCollaborator(user.userId)
  }, [user, fetchByCollaborator])

  useEffect(() => { reload() }, [reload])

  const filtered = useMemo(
    () => (filter === 'todas' ? applications : applications.filter((a) => a.status === filter)),
    [applications, filter],
  )

  const counts = useMemo(() => ({
    total: applications.length,
    activas: applications.filter((a) => ['pendiente', 'revisado', 'entrevista'].includes(a.status)).length,
    aceptadas: applications.filter((a) => a.status === 'aceptado').length,
  }), [applications])

  // Cuántas entrevistas pendientes de confirmar tiene el colaborador
  const pendingInterviews = useMemo(
    () => applications.filter((a) => a.interviewStatus === 'pendiente_confirmacion').length,
    [applications],
  )

  const handleWithdraw = async (a: Application) => {
    if (!window.confirm(`¿Retirar tu postulación a "${a.vacancyTitle || 'esta vacante'}"?`)) return
    try {
      await withdrawApplication(a._id)
      reload()
    } catch (e) {
      console.error('Error al retirar postulación:', e)
      window.alert('No se pudo retirar la postulación.')
    }
  }

  const handleInterviewResponse = async (a: Application, confirm: boolean) => {
    setWorking(true)
    try {
      await updateInterview(a._id, {
        interviewStatus: confirm ? 'confirmada' : 'rechazada',
      })
      reload()
      setInterviewDetail((prev) =>
        prev && prev._id === a._id
          ? { ...prev, interviewStatus: confirm ? 'confirmada' : 'rechazada' }
          : prev,
      )
    } catch (e) {
      console.error('Error al responder entrevista:', e)
      window.alert('No se pudo registrar tu respuesta.')
    } finally {
      setWorking(false)
    }
  }

  return (
    <ColaboradorLayout title="Mis postulaciones" subtitle="Da seguimiento al estado de tus postulaciones a vacantes">

      {/* Alerta de entrevistas pendientes de confirmar */}
      {pendingInterviews > 0 && (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlarmClock size={18} className="shrink-0 text-amber-600" />
          <span>
            Tienes <strong>{pendingInterviews}</strong> propuesta{pendingInterviews > 1 ? 's' : ''} de entrevista pendiente{pendingInterviews > 1 ? 's' : ''} de confirmar.
          </span>
          <button
            onClick={() => setFilter('entrevista')}
            className="ml-auto rounded-lg bg-amber-400 px-3 py-1 text-xs font-semibold text-slate-900 hover:bg-amber-300"
          >
            Ver
          </button>
        </div>
      )}

      {/* Resumen */}
      <div className="mb-5 grid grid-cols-3 gap-3">
        {[
          { label: 'Total', value: counts.total, icon: Briefcase, color: 'text-slate-700', bg: 'bg-slate-100' },
          { label: 'En proceso', value: counts.activas, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Aceptadas', value: counts.aceptadas, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${bg}`}>
              <Icon size={17} className={color} />
            </div>
            <p className="mt-2 text-xl font-bold text-slate-900">{loading ? '—' : value}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="mb-5 flex flex-wrap gap-1.5">
        {statusFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${filter === f.value ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-sm text-slate-400">Cargando postulaciones...</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <Briefcase size={22} />
          </div>
          <p className="mt-4 font-semibold text-slate-900">
            {applications.length === 0 ? 'Aún no te has postulado' : 'Sin postulaciones en este filtro'}
          </p>
          <p className="mt-1 text-sm text-slate-500">Explora las vacantes disponibles y postúlate al instante.</p>
          <Link
            to="/empleos"
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
          >
            <Search size={15} /> Buscar empleos
          </Link>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((a) => (
            <div key={a._id} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-bold text-slate-900">{a.vacancyTitle || 'Vacante'}</h3>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${APPLICATION_STATUS_STYLES[a.status]}`}>
                      {APPLICATION_STATUS_LABELS[a.status]}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-500 inline-flex items-center gap-1.5">
                    <Building2 size={13} /> {a.companyName || 'Empresa'}
                  </p>
                  <p className="mt-1 text-xs text-slate-400 inline-flex items-center gap-1.5">
                    <Calendar size={12} /> Postulado el {fmtDate(a.createdAt)}
                  </p>
                </div>

                <div className="flex flex-col items-end gap-1">
                  {a.status === 'aceptado' && (
                    <div className="rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700">
                      ¡Felicidades, fuiste aceptado!
                    </div>
                  )}
                  {a.interviewStatus && (
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${INTERVIEW_STATUS_STYLES[a.interviewStatus]}`}>
                      {INTERVIEW_STATUS_LABELS[a.interviewStatus]}
                    </span>
                  )}
                </div>
              </div>

              {/* Propuesta de entrevista pendiente de confirmar */}
              {a.status === 'entrevista' && a.interviewStatus === 'pendiente_confirmacion' && a.interviewDate && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <AlarmClock size={15} /> ¡La empresa propone una entrevista!
                  </p>
                  <div className="space-y-1 text-sm text-amber-700 mb-3">
                    <p className="flex items-center gap-2"><Calendar size={13} /> <strong>{fmtDateTime(a.interviewDate)}</strong></p>
                    {a.interviewAddress && <p className="flex items-center gap-2"><MapPin size={13} /> {a.interviewAddress}</p>}
                    {a.interviewPhone && <p className="flex items-center gap-2"><Phone size={13} /> {a.interviewPhone}</p>}
                    {a.interviewNotes && <p className="flex items-start gap-2"><Info size={13} className="mt-0.5 shrink-0" /> {a.interviewNotes}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleInterviewResponse(a, true)}
                      disabled={working}
                      className="flex-1 rounded-lg bg-emerald-600 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      <Check size={13} /> Confirmar asistencia
                    </button>
                    <button
                      onClick={() => handleInterviewResponse(a, false)}
                      disabled={working}
                      className="flex-1 rounded-lg border border-red-200 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      <X size={13} /> No puedo asistir
                    </button>
                  </div>
                </div>
              )}

              {/* Entrevista confirmada — mostrar detalles */}
              {a.status === 'entrevista' && a.interviewStatus === 'confirmada' && a.interviewDate && (
                <div className="mt-3 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5">
                  <p className="text-xs font-medium text-emerald-700 flex items-center gap-2">
                    <CheckCircle2 size={14} /> Entrevista confirmada: <strong>{fmtDateTime(a.interviewDate)}</strong>
                  </p>
                  <button onClick={() => setInterviewDetail(a)} className="text-xs text-emerald-600 underline">Ver detalles</button>
                </div>
              )}

              {/* Entrevista rechazada */}
              {a.interviewStatus === 'rechazada' && (
                <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-xs text-red-600">
                  Rechazaste la propuesta de entrevista. La empresa puede proponerte una nueva fecha.
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                <Link to="/empleos" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                  <Eye size={13} /> Ver vacante
                </Link>
                {a.interviewDate && (
                  <button onClick={() => setInterviewDetail(a)} className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-50">
                    <AlarmClock size={13} /> Datos de entrevista
                  </button>
                )}
                {['pendiente', 'revisado'].includes(a.status) && (
                  <button onClick={() => handleWithdraw(a)} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                    <Trash2 size={13} /> Retirar postulación
                  </button>
                )}
                {a.status === 'rechazado' && (
                  <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-400">
                    <XCircle size={13} /> No seleccionado
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ══════════════════════════════════════
          MODAL — Detalle de la entrevista
          ══════════════════════════════════════ */}
      {interviewDetail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setInterviewDetail(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between rounded-t-2xl bg-violet-700 px-6 py-4">
              <div>
                <h3 className="text-lg font-bold text-white">Detalles de la entrevista</h3>
                <p className="text-xs text-violet-200 mt-0.5">{interviewDetail.vacancyTitle} — {interviewDetail.companyName}</p>
              </div>
              <button onClick={() => setInterviewDetail(null)} className="rounded-lg p-1.5 text-violet-200 hover:bg-violet-600">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <Calendar size={16} className="mt-0.5 text-violet-500 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-slate-400 mb-0.5">Fecha y hora</p>
                  <p className="font-semibold text-slate-800">{fmtDateTime(interviewDetail.interviewDate)}</p>
                </div>
              </div>
              {interviewDetail.interviewAddress && (
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="mt-0.5 text-violet-500 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-0.5">Lugar</p>
                    <p className="text-slate-700">{interviewDetail.interviewAddress}</p>
                  </div>
                </div>
              )}
              {interviewDetail.interviewPhone && (
                <div className="flex items-start gap-3">
                  <Phone size={16} className="mt-0.5 text-violet-500 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-0.5">Teléfono de contacto</p>
                    <p className="text-slate-700">{interviewDetail.interviewPhone}</p>
                  </div>
                </div>
              )}
              {interviewDetail.interviewNotes && (
                <div className="flex items-start gap-3">
                  <Info size={16} className="mt-0.5 text-violet-500 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-0.5">Indicaciones adicionales</p>
                    <p className="text-slate-700 whitespace-pre-wrap">{interviewDetail.interviewNotes}</p>
                  </div>
                </div>
              )}
              {interviewDetail.interviewStatus && (
                <div className="pt-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${INTERVIEW_STATUS_STYLES[interviewDetail.interviewStatus]}`}>
                    Estado: {INTERVIEW_STATUS_LABELS[interviewDetail.interviewStatus]}
                  </span>
                </div>
              )}

              {/* Acciones si aún está pendiente de confirmar */}
              {interviewDetail.interviewStatus === 'pendiente_confirmacion' && (
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <button
                    onClick={() => handleInterviewResponse(interviewDetail, true)}
                    disabled={working}
                    className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                  >
                    Confirmar
                  </button>
                  <button
                    onClick={() => handleInterviewResponse(interviewDetail, false)}
                    disabled={working}
                    className="flex-1 rounded-lg border border-red-200 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    No puedo asistir
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </ColaboradorLayout>
  )
}

export default ColaboradorPostulaciones
